export interface SensorRules {
  stuck_hours: number;
  noise_gal: number;
  theft_gal: number;
  spike_gal: number;
  spike_pct: number;
  silence_hours: number;
}

export type SensorRulesForm = Record<keyof SensorRules, string>;

export interface SensorRuleError {
  key: keyof SensorRules;
  text: string;
}

type Unit = "hours" | "gal" | "%";

/** The fields in the order of the form, with the allowed range in the units of the form. */
export const RULE_FIELDS: { key: keyof SensorRules; label: string; unit: Unit; low: number; high: number; hint: string }[] = [
  {
    key: "stuck_hours",
    label: "Stuck after",
    unit: "hours",
    low: 12,
    high: 720,
    hint: "Open a Stuck issue when the level stays flat this long while the fill estimate says it should rise.",
  },
  {
    key: "noise_gal",
    label: "Noise margin",
    unit: "gal",
    low: 0.5,
    high: 100,
    hint: "A change smaller than this is sensor noise, not a change of the level.",
  },
  {
    key: "theft_gal",
    label: "Possible theft",
    unit: "gal",
    low: 5,
    high: 2000,
    hint: "Open a Possible theft issue for a drop larger than this with no visit, pickup or route stop near it.",
  },
  {
    key: "spike_gal",
    label: "Spike",
    unit: "gal",
    low: 5,
    high: 2000,
    hint: "Open a Spike issue for a jump larger than this, or than the share below, that comes back in the next two reports.",
  },
  { key: "spike_pct", label: "Spike share", unit: "%", low: 5, high: 100, hint: "The same jump as a share of the container size." },
  {
    key: "silence_hours",
    label: "Silence after",
    unit: "hours",
    low: 6,
    high: 720,
    hint: "Open a Silence issue when a sensor sends no report for this long.",
  },
];

const percent = (share: number) => String(Math.round(share * 1000) / 10);

export function rulesForm(rules: SensorRules): SensorRulesForm {
  return {
    stuck_hours: String(rules.stuck_hours),
    noise_gal: String(rules.noise_gal),
    theft_gal: String(rules.theft_gal),
    spike_gal: String(rules.spike_gal),
    spike_pct: percent(rules.spike_pct),
    silence_hours: String(rules.silence_hours),
  };
}

const amount = (value: string | number, unit: Unit) => (unit === "%" ? `${value}%` : `${value} ${unit}`);

export function ruleRangeText(key: keyof SensorRules, defaults: SensorRulesForm): string {
  const field = RULE_FIELDS.find((f) => f.key === key);
  if (!field) return "";
  return `From ${amount(field.low, field.unit)} to ${amount(field.high, field.unit)}. Default ${amount(defaults[key], field.unit)}.`;
}

function number(text: string, low: number, high: number, whole: boolean): number | null {
  const clean = text.trim().replace(/\s*%$/, "");
  const value = Number(clean);
  const ok = clean !== "" && Number.isFinite(value) && value >= low && value <= high && (!whole || Number.isInteger(value));
  return ok ? value : null;
}

/** The rules to save, or the first field that is wrong. */
export function rulesChange(form: SensorRulesForm): SensorRules | SensorRuleError {
  const values = {} as SensorRules;
  for (const { key, label, unit, low, high } of RULE_FIELDS) {
    const whole = unit === "hours";
    const value = number(form[key], low, high, whole);
    if (value === null) {
      const kind = whole ? "a whole number of hours" : "a number";
      return { key, text: `${label} must be ${kind} from ${amount(low, unit)} to ${amount(high, unit)}.` };
    }
    values[key] = value;
  }
  if (Math.min(values.theft_gal, values.spike_gal) <= values.noise_gal) {
    return { key: "noise_gal", text: "The noise margin must be below the theft and the spike limits." };
  }
  return { ...values, spike_pct: Math.round(values.spike_pct * 10) / 1000 };
}
